import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractNewsTopic } from "@similarweb/pro-api-client";

import { SimpleMultiselectFilter } from "../../../filters/SimpleMultiselectFilter";
import { topicFilterOptionTranslationKeyMap, topicTranslationKeyMap } from "../../utils/translationKeysConfig";
import { TOPICS } from "../constants";
import { useNewsAlertsFilterStore } from "../useNewsAlertsFilterStore";

import { getCountValue } from "./getCountValue";
import { useTopicCounts } from "./useTopicCounts";

export const Topic = () => {
    const { translate } = useTranslate();
    const selectedTopics = useNewsAlertsFilterStore((state) => state.selectedTopics);
    const isDefaultTopicsSelected = useNewsAlertsFilterStore((state) => state.isDefaultTopicsSelected);
    const setSelectedTopics = useNewsAlertsFilterStore((state) => state.setSelectedTopics);
    const { isLoading, topicCounts } = useTopicCounts();

    const options = useMemo(
        () =>
            TOPICS.map((topic: ActivationAlertsContractNewsTopic) => ({
                id: topic,
                label: translate(topicFilterOptionTranslationKeyMap[topic], {
                    count: getCountValue({
                        isDataLoaded: topicCounts !== undefined,
                        isLoading,
                        topicCount: topicCounts?.[topic],
                        translate,
                    }),
                }),
            })),
        [isLoading, topicCounts, translate],
    );

    const topicDisplayText = useMemo(() => {
        if (isDefaultTopicsSelected) {
            return translate("activation.alerts.filters.topic.all");
        }

        if (selectedTopics.size === 1) {
            const [firstTopic] = selectedTopics.values();

            return translate(topicTranslationKeyMap[firstTopic.id]);
        }

        return translate("activation.alerts.filters.topic.some", { selection: selectedTopics.size });
    }, [isDefaultTopicsSelected, selectedTopics, translate]);

    return (
        <SimpleMultiselectFilter
            appliedItems={selectedTopics}
            dataAutomation="topic-filter"
            isFilterSelected={!isDefaultTopicsSelected}
            onApplyChanges={setSelectedTopics}
            options={options}
        >
            {topicDisplayText}
        </SimpleMultiselectFilter>
    );
};
